import { useQuery } from '@tanstack/react-query';

import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import { SEARCH_QUERY_KEYS } from '../constants';
import { loadAutoCompleteSuggestions } from './DataLoading/LoadAutoCompleteSuggestions';
import { loadProductsSuggestions } from './DataLoading/LoadProductsSuggestions';
import { loadServicesSuggestions } from './DataLoading/LoadServicesSuggestions';
import { useLoadQueryKeys } from './useLoadQueryKeys';

export const useLoadNoSuggestionsRecommendations = () => {
    const { isLoading: isQueryKeysLoading, queryKeys } = useLoadQueryKeys();

    const segment = useHybrisSegment();
    const isB2cOrB2b = segment === 'b2c' || segment === 'b2b';

    const {
        data,
        isLoading: isRecommendationsLoading,
        error,
    } = useQuery({
        queryKey: [SEARCH_QUERY_KEYS.NO_SUGGESTIONS_RECOMMENDATIONS, segment],
        queryFn: async ({ signal }) => {
            const [
                { suggestions, error: suggestionsError },
                { products, error: productsError },
                { services, error: servicesError },
            ] = await Promise.all([
                loadAutoCompleteSuggestions({ searchText: '', segment, queryKeys, signal }),
                isB2cOrB2b
                    ? loadProductsSuggestions({ searchText: '', segment, queryKeys, signal })
                    : { products: undefined, error: undefined },
                loadServicesSuggestions({ searchText: '', segment, queryKeys, signal }),
            ]);

            if (suggestionsError && productsError && servicesError) {
                throw suggestionsError;
            }

            return { suggestions, products, services };
        },
        enabled: !!queryKeys,
        staleTime: 5000,
    });
    useLogError(error, DDComponent.Search);

    return {
        isLoading: isQueryKeysLoading || isRecommendationsLoading,
        error,
        suggestions: data?.suggestions,
        products: data?.products,
        services: data?.services,
    };
};
